"use client"

import { motion, AnimatePresence, useReducedMotion } from "motion/react"
import type { DethatchingResult, ThatchDepthBand } from "@/lib/dethatching-calculator"

/**
 * The verdict illustration (Claude Design DethatchingPage, section 3): the same
 * turf cross-section as the selector, scaled up, with the thatch band animating
 * to the chosen depth and tinting to the verdict tone. Overlays: a green check
 * (healthy), a spring-tine rake tipping in (dethatch), or vertical-mower blades
 * (urgent). Pure SVG + CSS transitions + motion entrances - no canvas.
 */

const THATCH_H: Record<ThatchDepthBand, number> = {
  "under-quarter": 8,
  "quarter-to-half": 16,
  "half-to-one": 32,
  "over-one": 58,
}

const BAND_FILL: Record<DethatchingResult["verdict"], string> = {
  monitor: "#84cc16",
  dethatch: "#f59e0b",
  "dethatch-urgent": "#b91c1c",
}

// Scoped to only the SVG geometry/presentation attributes that actually change
// with the thatch depth (was `transition: all`, which animates off-GPU — AUDIT §5).
// Curve tokenized to --ease-standard with a fallback to the original curve so the
// transition never breaks if the token isn't defined at this scope (AUDIT §7).
const GEOM_EASE = "var(--ease-standard, cubic-bezier(0.4,0,0.2,1))"
const GEOM_TRANSITION = ["d", "y", "y1", "y2", "cy", "height"]
  .map((prop) => `${prop} 400ms ${GEOM_EASE}`)
  .join(", ")

export function VerdictCrossSection({
  verdict,
  band,
  thatchInches,
}: {
  verdict: DethatchingResult["verdict"]
  band: ThatchDepthBand
  thatchInches: string
}) {
  const reduce = useReducedMotion()
  const thatchH = THATCH_H[band]
  const soilY = 96 + thatchH
  const soilH = Math.max(0, 210 - soilY)
  const squigY = 96 + thatchH * 0.55
  const rulerTextY = 96 + thatchH / 2 + 5

  return (
    <svg viewBox="0 0 320 230" className="block w-full" aria-hidden="true">
      {verdict === "monitor" && (
        <motion.ellipse
          cx="160"
          cy="110"
          rx="150"
          ry="90"
          fill="#22c55e"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.1 }}
          transition={{ duration: 0.4 }}
        />
      )}
      {/* blades */}
      <g stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" fill="none">
        <path d="M24 96c0-10 3-17 6-22M46 96c0-12 3-19 6-24M68 96c0-10 3-16 6-21M92 96c0-12 3-20 6-25M116 96c0-10 3-17 6-22M140 96c0-12 3-18 6-23M164 96c0-10 3-16 6-21M188 96c0-12 3-20 6-25M212 96c0-10 3-17 6-22M236 96c0-12 3-18 6-23M260 96c0-10 3-16 6-21M284 96c0-11 3-18 6-23" />
      </g>
      <line x1="12" y1="96" x2="308" y2="96" stroke="#15803d" strokeWidth="2.4" strokeLinecap="round" />

      {/* thatch band: ONE stable element for every verdict so height and fill
          transition continuously (a plain-rect / motion-rect swap at the urgent
          threshold would remount the node and skip the biggest depth change).
          The urgent pulse animates opacity as a loop; other verdicts hold a
          steady 0.5. */}
      <motion.rect
        x="12"
        y="96"
        width="296"
        height={thatchH}
        rx="3"
        fill={BAND_FILL[verdict]}
        style={{ transition: `${GEOM_TRANSITION}, fill 300ms ease` }}
        animate={
          verdict === "dethatch-urgent"
            ? { opacity: [0.55, 0.85, 0.55] }
            : { opacity: 0.5 }
        }
        transition={
          verdict === "dethatch-urgent"
            ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
            : { duration: 0.3 }
        }
      />
      <g stroke="#92400e" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.8">
        <path
          d={`M28 ${squigY}c6-4 12-4 18-2M84 ${squigY}c6-4 12-4 18-2M150 ${squigY}c6-4 12-4 18-2M216 ${squigY}c6-4 12-4 18-2M274 ${squigY}c6-4 12-4 18-2`}
          style={{ transition: GEOM_TRANSITION }}
        />
      </g>

      {/* soil */}
      <rect x="12" y={soilY} width="296" height={soilH} rx="3" fill="#451a03" opacity="0.8" style={{ transition: GEOM_TRANSITION }} />
      <g fill="#92400e">
        <circle cx="52" cy={soilY + soilH * 0.4} r="2.4" style={{ transition: GEOM_TRANSITION }} />
        <circle cx="120" cy={soilY + soilH * 0.65} r="2" style={{ transition: GEOM_TRANSITION }} />
        <circle cx="196" cy={soilY + soilH * 0.4} r="2.4" style={{ transition: GEOM_TRANSITION }} />
        <circle cx="262" cy={soilY + soilH * 0.65} r="2" style={{ transition: GEOM_TRANSITION }} />
      </g>
      <g stroke="#fef3c7" strokeWidth="1.2" strokeLinecap="round" opacity="0.5" fill="none">
        <path d={`M70 ${soilY}c-2 8-1 14 1 20M160 ${soilY}c2 8 1 14-1 20M250 ${soilY}c-2 8-1 14 1 20`} style={{ transition: GEOM_TRANSITION }} />
      </g>

      {/* depth ruler */}
      <g>
        <line x1="314" y1="96" x2="314" y2={soilY} stroke="#0f1d14" strokeWidth="1.4" style={{ transition: GEOM_TRANSITION }} />
        <line x1="310" y1="96" x2="318" y2="96" stroke="#0f1d14" strokeWidth="1.4" />
        <line x1="310" y1={soilY} x2="318" y2={soilY} stroke="#0f1d14" strokeWidth="1.4" style={{ transition: GEOM_TRANSITION }} />
      </g>
      <text
        x="308"
        y={rulerTextY}
        textAnchor="end"
        fontSize="13"
        fontWeight="700"
        fill="#0f1d14"
        fontFamily="inherit"
        style={{ transition: GEOM_TRANSITION }}
      >
        {thatchInches}
      </text>

      <AnimatePresence mode="wait">
        {verdict === "monitor" && (
          <motion.g
            key="healthy"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 6 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <circle cx="282" cy="44" r="16" fill="#16a34a" />
            <path d="M275 44.5l4.5 4.5 9-9.5" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </motion.g>
        )}
        {verdict === "dethatch" && (
          <motion.g
            key="rake"
            initial={reduce ? { opacity: 0 } : { opacity: 0, rotate: -6, y: -4 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, rotate: 0, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            style={{ transformOrigin: "250px 40px" }}
          >
            <path d="M292 14L236 78" stroke="#0f1d14" strokeWidth="4" strokeLinecap="round" />
            <path d="M286 8l14 12" stroke="#0f1d14" strokeWidth="3.5" strokeLinecap="round" />
            <path d="M212 80h40" stroke="#0f1d14" strokeWidth="4" strokeLinecap="round" />
            <path
              d="M214 82c-2 8-6 13-11 16M226 82c-1.5 8-5 14-9 17.5M238 82c-1 8-3.5 14-6.5 18M250 82c-0.8 8-2.8 14.5-5.5 19"
              stroke="#0f1d14"
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
            />
            <path d="M186 104c5-6 12-7.5 18-4M196 110c6-5 13-6 19-2.5" stroke="#92400e" strokeWidth="3" strokeLinecap="round" fill="none" />
          </motion.g>
        )}
        {verdict === "dethatch-urgent" && (
          <motion.g
            key="verticutter"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: -4 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          >
            <circle cx="246" cy="86" r="26" fill="#fff" stroke="#0f1d14" strokeWidth="3" />
            <g stroke="#0f1d14" strokeWidth="3" strokeLinecap="round">
              <path d="M246 60v52M224 74l44 24M224 98l44-24" />
            </g>
            <circle cx="246" cy="86" r="5" fill="#0f1d14" />
            <path d="M278 70l22-22M292 74l14-14" stroke="#0f1d14" strokeWidth="3.5" strokeLinecap="round" />
            <path d="M212 120c4-5 9-6 14-3M224 128c4-4 9-5 14-2" stroke="#92400e" strokeWidth="3" strokeLinecap="round" fill="none" />
          </motion.g>
        )}
      </AnimatePresence>
    </svg>
  )
}
